import { ComponentType } from 'react';
import { MdOutlineDashboard, MdOutlineSettings } from 'react-icons/md';
import {
  PiBagSimple,
  PiBuildingsLight,
  PiCreditCard,
  PiHandshake,
  PiReceipt,
  PiShieldCheck,
  PiUserPlusLight,
  PiUsersFourLight,
} from 'react-icons/pi';
import Contacts from '@/contacts/Contacts';
import ContactListPage from '@/pages/contacts/ContactListPage';
import ContactDetailPage from '@/pages/contacts/ContactDetailPage';
import AccountDetailPage from '@/pages/accounts/AccountDetailPage';
import AccountsListPage from '@/pages/accounts/AccountsListPage';
import InvoicesPageWrapper from '@/pages/invoices/InvoicesPageWrapper';
import {
  INVOICING_COMPANIES_ALL,
  PAYERS_ALL,
  PERMISSIONS,
  PROPOSALS_READ_SHOP,
  Permission,
  REFERRING_AGENTS_ALL,
  REFERRING_AGENTS_READ,
} from '@/models/permissions';
import Companies from '@/pages/Companies';
import CommercialProposalsPage from '@/pages/commercial-proposals/CommercialProposalsPage';
import Dashboard from '@/pages/dashboard/Dashboard';
import InvoicingCompaniesPage from '@/pages/invoicing-companies/InvoicingCompaniesPage';
import PayersPage from '@/pages/payers/PayersPage';
import PayerDetail from '@/pages/payers/PayerDetail';
import LeadDetailPage from '@/pages/leads/LeadDetailPage';
import LeadListPage from '@/pages/leads/LeadListPage';
import Products from '@/pages/products/Products';
import ReferringAgentsPage from '@/pages/referring-agents/ReferringAgentsPage';
import RenewalsPage from '@/pages/renewals/RenewalsPage';
import OffersPage from '@/pages/offers/OffersPage';
import SplitPlansPage from '@/pages/split-plans/SplitPlansPage';
import JurisdictionFeesPage from '@/pages/settings/JurisdictionFeesPage';
import type { NavCounterKey } from '@/models/nav-counters';
import Users from '@/pages/admin/Users';
import Roles from '@/pages/admin/Roles';

export interface RouteConfig {
  path: string;
  component: ComponentType<any>;
  permissions?: Permission[];
  requireAll?: boolean;

  /**
   * Optional menu configuration. If provided, this route will appear in the navigation menu.
   */
  menu?: {
    label: string;
    icon: ComponentType;

    /**
     * Optional nav-counter key. When set, the menu entry renders a badge with
     * the matching count from `useNavCounters()` (hidden when zero or absent).
     */
    badgeKey?: NavCounterKey;
  };
}

/**
 * Application route configuration
 *
 * Each route can specify:
 * - permissions: Array of required permissions
 * - requireAll: If true, user must have ALL permissions. If false, user needs ANY one permission.
 * - menu: If provided, the route will appear in the navigation menu with the specified label and icon
 *
 * Routes without permissions are accessible to all authenticated users.
 */
export const routes: RouteConfig[] = [
  {
    path: '/dashboard',
    component: Dashboard,
    permissions: [PERMISSIONS.DASHBOARD_READ],
    menu: {
      label: 'Dashboard',
      icon: MdOutlineDashboard,
    },
  },
  {
    path: '/offers',
    component: OffersPage,
    permissions: [PERMISSIONS.OFFERS_READ, PERMISSIONS.OFFERS_SEND],
    requireAll: false,
    menu: {
      label: 'Offers',
      icon: PiBagSimple,
      badgeKey: 'offersAwaitingSalesRep',
    },
  },
  {
    path: '/split-plans',
    component: SplitPlansPage,
    permissions: ['offers:approve-split' as Permission],
    menu: {
      label: 'Split Plans',
      icon: PiCreditCard,
      badgeKey: 'splitPlansPendingReview',
    },
  },
  {
    path: '/contacts/:id',
    component: ContactDetailPage,
    permissions: [
      PERMISSIONS.CONTACTS_READ,
      'contacts:read-all' as Permission,
      PERMISSIONS.CONTACTS_ALL,
    ],
  },
  {
    path: '/accounts/:id',
    component: AccountDetailPage,
    permissions: [
      PERMISSIONS.CONTACTS_READ,
      'contacts:read-all' as Permission,
      PERMISSIONS.CONTACTS_ALL,
    ],
  },
  {
    path: '/accounts',
    component: AccountsListPage,
    permissions: [
      PERMISSIONS.CONTACTS_READ,
      'contacts:read-all' as Permission,
      PERMISSIONS.CONTACTS_ALL,
    ],
    menu: {
      label: 'Accounts',
      icon: PiBuildingsLight,
    },
  },
  {
    path: '/contacts',
    component: ContactListPage,
    permissions: [
      PERMISSIONS.CONTACTS_READ,
      'contacts:read-all' as Permission,
      PERMISSIONS.CONTACTS_ALL,
    ],
    menu: {
      label: 'Contacts',
      icon: PiUsersFourLight,
    },
  },
  {
    path: '/contacts-monday',
    component: Contacts,
    permissions: [PERMISSIONS.CONTACTS_READ],
  },
  {
    path: '/leads/:id',
    component: LeadDetailPage,
    permissions: [
      PERMISSIONS.LEADS_READ,
      'leads:read-all' as Permission,
      PERMISSIONS.LEADS_ALL,
    ],
  },
  {
    path: '/leads',
    component: LeadListPage,
    permissions: [
      PERMISSIONS.LEADS_READ,
      'leads:read-all' as Permission,
      PERMISSIONS.LEADS_ALL,
    ],
    menu: {
      label: 'Leads',
      icon: PiUserPlusLight,
    },
  },
  {
    path: '/referring-agents',
    component: ReferringAgentsPage,
    permissions: [REFERRING_AGENTS_READ, REFERRING_AGENTS_ALL],
    menu: {
      label: 'Referring Agents',
      icon: PiHandshake,
    },
  },
  {
    path: '/companies',
    component: Companies,
    permissions: [PERMISSIONS.COMPANIES_READ],
    menu: {
      label: 'Companies',
      icon: PiBagSimple,
    },
  },
  {
    path: '/products',
    component: Products,
    permissions: [PERMISSIONS.SERVICES_READ],
    menu: {
      label: 'Product List',
      icon: PiBagSimple,
    },
  },
  {
    path: '/invoices',
    component: InvoicesPageWrapper,
    permissions: [PERMISSIONS.INVOICES_READ],
    menu: {
      label: 'Invoices',
      icon: PiReceipt,
    },
  },
  {
    path: '/commercial-proposals',
    component: CommercialProposalsPage,
    permissions: [PERMISSIONS.PROPOSALS_READ, PROPOSALS_READ_SHOP],
    requireAll: false,
    menu: {
      label: 'Commercial Proposals',
      icon: PiBagSimple,
    },
  },
  {
    path: '/renewals',
    component: RenewalsPage,
    permissions: [PERMISSIONS.RENEWALS_READ],
    menu: {
      label: 'Renewals',
      icon: PiReceipt,
    },
  },
  {
    path: '/invoicing-companies',
    component: InvoicingCompaniesPage,
    permissions: [INVOICING_COMPANIES_ALL as Permission],
    menu: {
      label: 'Invoicing Companies',
      icon: PiBuildingsLight,
    },
  },
  {
    path: '/payers/:id',
    component: PayerDetail,
    permissions: [PERMISSIONS.PAYERS_READ, PAYERS_ALL as Permission],
  },
  {
    path: '/payers',
    component: PayersPage,
    permissions: [PERMISSIONS.PAYERS_READ, PAYERS_ALL as Permission],
    menu: { label: 'Payers', icon: PiShieldCheck },
  },
  {
    path: '/settings/jurisdiction-fees',
    component: JurisdictionFeesPage,
    permissions: [PERMISSIONS.RENEWALS_WRITE],
    menu: {
      label: 'Jurisdiction Fees',
      icon: MdOutlineSettings,
    },
  },
  {
    path: '/admin/users',
    component: Users,
    permissions: [PERMISSIONS.RBAC_READ],
    menu: {
      label: 'Users',
      icon: PiUsersFourLight,
    },
  },
  {
    path: '/admin/roles',
    component: Roles,
    permissions: [PERMISSIONS.RBAC_READ],
    menu: {
      label: 'Roles',
      icon: PiShieldCheck,
    },
  },
];

export interface MenuItem {
  label: string;
  to: string;
  icon: ComponentType;
  badgeKey?: NavCounterKey;
  permissions?: Permission[];
  requireAll?: boolean;
}

export const menuItems: MenuItem[] = routes
  .filter((route) => route.menu)
  .map((route) => ({
    label: route.menu!.label,
    to: route.path,
    icon: route.menu!.icon,
    badgeKey: route.menu!.badgeKey,
    permissions: route.permissions,
    requireAll: route.requireAll,
  }));

export interface MenuGroup {
  title: string | null;
  items: MenuItem[];
}

const GROUPS: { title: string | null; paths: string[] }[] = [
  { title: null, paths: ['/dashboard'] },
  {
    title: 'CRM',
    paths: [
      '/leads',
      '/contacts',
      '/accounts',
      '/companies',
      '/referring-agents',
    ],
  },
  {
    title: 'Sales',
    paths: ['/offers', '/split-plans', '/commercial-proposals', '/renewals'],
  },
  { title: 'Billing', paths: ['/invoices', '/invoicing-companies', '/payers'] },
  { title: 'Catalog', paths: ['/products', '/settings/jurisdiction-fees'] },
  { title: 'Administration', paths: ['/admin/users', '/admin/roles'] },
];

export const menuGroups: MenuGroup[] = GROUPS.map((group) => ({
  title: group.title,
  items: group.paths
    .map((path) => menuItems.find((item) => item.to === path))
    .filter((item): item is MenuItem => !!item),
}));
